import { apiClient } from '@/lib/api/client'

/**
 * CSV Utility Functions
 * Reusable functions for CSV export, import, and template download
 */

export interface CSVExportOptions {
  endpoint: string
  selectedIds?: number[]
  activeTab?: 'all' | 'trash'
  searchQuery?: string
  csvHeaders: string[]
  dataMapper: (item: any) => any[]
  filename?: string
  onSuccess?: (count: number) => void
  onError?: (error: any) => void
  onWarning?: (message: string) => void
  showError?: (error: any) => void
  showSuccess?: (title: string, message: string) => void
  showWarning?: (title: string, message?: string) => void
}

export interface CSVImportOptions {
  endpoint: string
  file: File
  /** Split large CSVs into sequential uploads (keeps bcrypt-heavy imports under server limits). */
  chunkSize?: number
  onSuccess?: (data: any) => void
  onError?: (error: any) => void
  showError?: (error: any) => void
  showSuccess?: (title: string, message: string) => void
  showWarning?: (title: string, message?: string) => void
}

export interface CSVTemplateOptions {
  csvHeaders: string[]
  exampleRow: string[]
  comments?: string[]
  filename?: string
  onSuccess?: () => void
  onError?: (error: any) => void
  showError?: (error: any) => void
  showSuccess?: (title: string, message: string) => void
}

/**
 * Escape CSV values to handle special characters
 */
export const escapeCSV = (value: any): string => {
  const stringValue = String(value ?? '')
  // Handle quotes, newlines, commas
  if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('\r')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }
  return stringValue
}

/**
 * Export data to CSV file
 * - If selectedIds provided: exports only selected rows
 * - If no selectedIds: exports ALL data from database (no filters, no pagination)
 */
export const exportToCSV = async (options: CSVExportOptions): Promise<void> => {
  const {
    endpoint,
    selectedIds = [],
    activeTab,
    searchQuery,
    csvHeaders,
    dataMapper,
    filename,
    onSuccess,
    onError,
    onWarning,
    showError,
    showSuccess,
    showWarning: showWarningToast,
  } = options

  const hasSelectedRows = selectedIds.length > 0

  try {
    let data: any[] = []

    if (hasSelectedRows) {
      // Export only selected rows
      try {
        const userPromises = selectedIds.map((id: number) =>
          apiClient.get(`${endpoint}/${id}`).catch(() => null)
        )
        const userResponses = await Promise.all(userPromises)
        data = userResponses
          .map((response) => response?.data?.data || response?.data)
          .filter((item) => item !== null && item !== undefined)
      } catch (error) {
        // Fallback: fetch all and filter
        const finalEndpoint = activeTab === 'trash' && endpoint.includes('/users')
          ? endpoint.replace('/users', '/archived/users')
          : endpoint
        const params: Record<string, any> = {
          per_page: 10000,
          ...(searchQuery && { search: searchQuery }),
        }
        const response = await apiClient.get(finalEndpoint, { params })
        const allData = response.data.data || []
        data = allData.filter((item: any) => selectedIds.includes(item.id))
      }
    } else {
      // No selection: export ALL data from database (no filters, no search, no pagination limits)
      const finalEndpoint = activeTab === 'trash' && endpoint.includes('/users')
        ? endpoint.replace('/users', '/archived/users')
        : endpoint

      // Fetch all data without any filters or search constraints
      let allData: any[] = []
      let currentPage = 1
      let hasMorePages = true

      while (hasMorePages) {
        const params: Record<string, any> = {
          page: currentPage,
          per_page: 1000, // Fetch in chunks of 1000
        }

        const response = await apiClient.get(finalEndpoint, { params })
        const pageData = response.data.data || []
        const meta = response.data.meta || {}

        allData = [...allData, ...pageData]

        // Check if there are more pages
        hasMorePages = currentPage < (meta.last_page || 1)
        currentPage++

        // Safety limit to prevent infinite loops
        if (currentPage > 1000) {
          break
        }
      }

      data = allData
    }

    if (data.length === 0) {
      onWarning?.('No data to export')
      showWarningToast?.('Export', 'No data to export')
      return
    }

    // Convert data to CSV
    const csvRows = data.map(dataMapper)

    // Combine headers and rows
    const csvContent = [
      csvHeaders.map(escapeCSV).join(','),
      ...csvRows.map((row: any[]) => row.map(escapeCSV).join(',')),
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    const timestamp = new Date().toISOString().split('T')[0]
    const tabLabel = activeTab === 'trash' ? '_trash' : ''
    const selectionLabel = hasSelectedRows ? `_${selectedIds.length}_selected` : '_all'
    const finalFilename = filename || `export${tabLabel}${selectionLabel}_${timestamp}.csv`
    
    link.setAttribute('href', url)
    link.setAttribute('download', finalFilename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    const exportMessage = hasSelectedRows
      ? `Successfully exported ${data.length} selected item(s)`
      : `Successfully exported ${data.length} item(s)`
    
    showSuccess?.('Export Complete', exportMessage)
    onSuccess?.(data.length)
  } catch (error: any) {
    showError?.(error)
    onError?.(error)
  }
}

export interface CSVRowsExportOptions {
  csvHeaders: string[]
  rows: any[][]
  filename?: string
  showError?: (error: any) => void
  showSuccess?: (title: string, message?: string) => void
  showWarning?: (title: string, message?: string) => void
}

/**
 * Build and download a CSV from pre-built rows (e.g. flattened relations).
 */
export const exportRowsToCSV = (options: CSVRowsExportOptions): void => {
  const { csvHeaders, rows, filename, showError, showSuccess, showWarning } = options

  try {
    if (rows.length === 0) {
      showWarning?.('Export', 'No data to export')
      return
    }

    const csvContent = [
      csvHeaders.map(escapeCSV).join(','),
      ...rows.map((row) => row.map(escapeCSV).join(',')),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    const timestamp = new Date().toISOString().split('T')[0]
    const finalFilename = filename || `export_${timestamp}.csv`

    link.setAttribute('href', url)
    link.setAttribute('download', finalFilename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    showSuccess?.('Export Complete', `Successfully exported ${rows.length} row(s)`)
  } catch (error: any) {
    showError?.(error)
  }
}

/**
 * Read CSV bytes into memory immediately so the File handle can be released
 * (clearing <input type="file"> invalidates the original File and causes NotReadableError).
 */
const readCsvContent = async (file: File): Promise<{ name: string; content: string }> => {
  const buffer = await file.arrayBuffer()
  const raw = new TextDecoder('utf-8').decode(buffer)
  return {
    name: file.name,
    content: raw.replace(/^\uFEFF/, ''),
  }
}

/**
 * Split CSV text into smaller File blobs (header + N data rows) for chunked upload.
 */
const splitCsvContentIntoChunks = (name: string, content: string, chunkSize: number): File[] => {
  const lines = content.split(/\r?\n/)
  if (lines.length === 0) {
    return [new File([content], name, { type: 'text/csv' })]
  }

  const header = lines[0]
  const dataLines = lines.slice(1).filter((line) => line.trim() !== '')
  if (dataLines.length <= chunkSize) {
    return [new File([content], name, { type: 'text/csv' })]
  }

  const chunks: File[] = []
  for (let i = 0; i < dataLines.length; i += chunkSize) {
    const chunkLines = [header, ...dataLines.slice(i, i + chunkSize)]
    chunks.push(
      new File([chunkLines.join('\n')], name.replace(/\.csv$/i, '') + `_part${chunks.length + 1}.csv`, {
        type: 'text/csv',
      })
    )
  }
  return chunks
}

/**
 * Import data from CSV file
 */
export const importFromCSV = async (options: CSVImportOptions): Promise<any> => {
  const { 
    endpoint, 
    file, 
    chunkSize,
    onSuccess, 
    onError,
    showError,
    showSuccess,
    showWarning,
  } = options

  // Validate file type
  if (!file.name.endsWith('.csv')) {
    showError?.({ message: 'Please upload a CSV file. Only .csv files are supported.' })
    throw new Error('Invalid file type')
  }

  // Validate file size (10MB max)
  const maxSize = 10 * 1024 * 1024
  if (file.size > maxSize) {
    showError?.({ message: `File size must be less than 10MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB` })
    throw new Error('File too large')
  }

  // Validate file is not empty
  if (file.size === 0) {
    showError?.({ message: 'The uploaded file is empty. Please upload a valid CSV file.' })
    throw new Error('Empty file')
  }

  const postImportChunk = async (chunkFile: File, rowOffset = 1) => {
    const formData = new FormData()
    formData.append('file', chunkFile)
    if (rowOffset > 1) {
      formData.append('row_offset', String(rowOffset))
    }

    // Let the apiClient interceptor clear default application/json so the
    // browser sets multipart/form-data with a boundary (required for Laravel).
    const response = await apiClient.post(endpoint, formData, {
      timeout: 300000,
    })

    return response.data.data || response.data
  }

  try {
    // Snapshot content first — never read from the live <input> File after React updates/clears it.
    const { name, content } = await readCsvContent(file)
    const size = chunkSize && chunkSize > 0 ? chunkSize : 0
    const chunks =
      size > 0
        ? splitCsvContentIntoChunks(name, content, size)
        : [new File([content], name, { type: 'text/csv' })]

    let imported = 0
    let updated = 0
    let skipped = 0
    const errors: string[] = []

    for (let i = 0; i < chunks.length; i++) {
      const rowOffset = size > 0 ? 1 + i * size : 1
      const data = await postImportChunk(chunks[i], rowOffset)
      imported += data.imported || data.success || 0
      updated += data.updated || 0
      skipped += data.skipped || 0
      if (Array.isArray(data.errors)) {
        errors.push(...data.errors)
      } else if (Array.isArray(data.failed)) {
        errors.push(...data.failed)
      }
    }

    const data = { imported, updated, skipped, errors }

    const parts: string[] = []
    if (imported > 0) {
      parts.push(`${imported} imported`)
    }
    if (updated > 0) {
      parts.push(`${updated} updated`)
    }
    let message =
      parts.length > 0
        ? `Successfully ${parts.join(', ')}`
        : 'No items were imported or updated.'
    if (chunks.length > 1) {
      message += ` across ${chunks.length} batches`
    }
    if (skipped > 0) {
      message += `. ${skipped} item(s) skipped.`
    }
    if (errors.length > 0) {
      message += ` ${errors.length} error(s) occurred.`

      if (process.env.NODE_ENV === 'development') {
        console.error('[Import Errors]', errors)
      }
    }

    if (imported > 0 || updated > 0) {
      showSuccess?.('Import Complete', message)
      onSuccess?.(data)
      return data
    } else {
      const warningMessage =
        errors.length > 0
          ? errors.slice(0, 5).join(' ')
          : message || 'No items were imported. Please check the file format.'
      showWarning?.('Import Complete', warningMessage)
      onSuccess?.(data)
      return data
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to import items. Please check the file format and try again.'
    showError?.({ message: errorMessage })

    if (process.env.NODE_ENV === 'development') {
      console.error('[Import Error]', error)
    }
    
    onError?.(error)
    throw error
  }
}

/**
 * Download CSV template file
 */
export const downloadCSVTemplate = (options: CSVTemplateOptions): void => {
  const { 
    csvHeaders, 
    exampleRow, 
    comments = [], 
    filename = 'template.csv', 
    onSuccess, 
    onError,
    showError,
    showSuccess,
  } = options

  try {
    // Create CSV content
    const csvContent = [
      csvHeaders.map(escapeCSV).join(','),
      exampleRow.map(escapeCSV).join(','),
      ...comments.map((comment) => `# ${comment}`),
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    showSuccess?.('Template Downloaded', 'CSV template downloaded successfully')
    onSuccess?.()
  } catch (error: any) {
    showError?.({ message: 'Failed to download template: ' + (error.message || 'Unknown error') })
    onError?.(error)
  }
}

